import { OrderLineItem } from '../../queries/shopify/fragments/OrderLineItems';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ManufacturerEmail, { ManufacturerEmailProps } from '../../components/email/ManufacturerEmail';
import { Manufacturer } from '../types';
import useSendEmail from '../../queries/email/sendEmail';
import { SendStatus } from '../../models/sendStatus';
import { ManufacturerStatus } from '../../models/orderStatus';
import { Item } from '../../models/item';

const ManufacturerEmails: Record<Manufacturer, string[]> = {
  'pear sox': import.meta.env.EMAIL_PEARSOX
    ? import.meta.env.EMAIL_PEARSOX.split(',')
    : [],
  'mk socks': import.meta.env.EMAIL_MKSOCKS
    ? import.meta.env.EMAIL_MKSOCKS.split(',')
    : [],
  uniforms: import.meta.env.EMAIL_UNIFORMS
    ? import.meta.env.EMAIL_UNIFORMS.split(',')
    : [],
};

type SendItemsToManufacturer = {
  orderName: string;
  manufacturer: Manufacturer;
  items: Item[];
  notes: { [key: string]: string };
};

export const useSendItemsToManufacturer = () => {
  const sendEmail = useSendEmail();
  return async ({
    orderName,
    manufacturer,
    items,
    notes,
  }: SendItemsToManufacturer): Promise<ManufacturerStatus> => {
    const subject = `PO ${orderName}`;
    const content = generateEmailContent(orderName, items, notes);
    const recipients = getRecipients(manufacturer);

    let newStatus: ManufacturerStatus = {
      status: SendStatus.notSent, 
      time: Date.now(), 
      lineItems: items.map((item) => item.id),
      notes,
    };
    try {
      await sendEmail(
        recipients.to,
        subject,
        content,
        recipients.cc,
        recipients.bcc,
      );
      console.info('Sent manufacturer email:', subject, recipients);
      newStatus = {
        ...newStatus,
        status: SendStatus.sent,
        emails: recipients.to,
      };
    } catch (e) {
      console.warn(
        'Failed to send manufacturer email:',
        subject,
        recipients,
        e.message,
      );
      newStatus = {
        ...newStatus,
        status: SendStatus.failedToSend,
        emails: e.emails,
      };
    }
    return newStatus;
  };
};

function getRecipients(manufacturer: Manufacturer) {
  return {
    cc: import.meta.env.EMAIL_CC ? import.meta.env.EMAIL_CC.split(',') : [],
    bcc: import.meta.env.EMAIL_BCC ? import.meta.env.EMAIL_BCC.split(',') : [],
    to: ManufacturerEmails[manufacturer],
  };
}

function generateEmailContent(orderId: string, items: Item[], notes: { [key: string]: string }) {
  const doctype =
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">';
  return (
    doctype +
    renderToStaticMarkup(
      <ManufacturerEmail orderId={orderId} items={items} notes={notes} />,
    )
  );
}

export default useSendItemsToManufacturer;
